import { createContext, useContext, useEffect, useState } from "react";

type Theme = string;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  userTheme?: Theme | null;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  userTheme = null,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(
    () =>
      userTheme || (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  // ⬇️ Mettre à jour le theme quand userTheme change (ex: après login)
  useEffect(() => {
    if (userTheme && userTheme !== theme) {
      setThemeState(userTheme);
      localStorage.setItem(storageKey, userTheme);
    }
  }, [userTheme]);

  // ⬇️ Appliquer le thème au <html>
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeProviderContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
